import { Fragment, useEffect, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowsLeftRight, faArrowsUpDown, faClipboardList } from "@fortawesome/free-solid-svg-icons";
import { CellDef, stateLabelOf } from "../../Functions/htScenarios";

// Form fields shared by the "Antrenare" and "Învățare" create-session forms:
// the custom dropdown (plain list or 3-column matrix), the operative-state
// picker and the start -> target state chip.

export interface Opt { id: string; label: string; disabled?: boolean; }
// `scope` marks the whole-station / semi-station / busbar groups, which get an
// accent in the matrix grid so they read apart from the individual cells.
// `footer` groups render below the grid, pinned to the first column (used for
// the "Liber" scope so it sits bottom-left). `col` (1..3) explicitly places a
// group in one of the three matrix columns instead of auto-balancing.
export interface OptGroup { group?: string; items: Opt[]; scope?: boolean; footer?: boolean; col?: number; }

// Open/close state for a panel that unfolds out of its input box. The panel is
// kept mounted for the length of the closing animation (CLOSE_MS), so it folds
// back into the field instead of vanishing.
const CLOSE_MS = 140;
export const useExpandable = () => {
    const [open, setOpen] = useState(false);
    const [closing, setClosing] = useState(false);
    const timer = useRef<any>(null);
    useEffect(() => () => clearTimeout(timer.current), []);
    const close = () => {
        if (!open) return;
        setClosing(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => { setClosing(false); setOpen(false); }, CLOSE_MS);
    };
    const toggle = () => {
        if (open && !closing) return close();
        clearTimeout(timer.current);
        setClosing(false);
        setOpen(true);
    };
    // `panelClass` carries the closing animation; `open` also drives the caret flip.
    return { open, closing, toggle, close, panelClass: closing ? " closing" : "" };
};

// Custom (non-native) dropdown. Closes on outside click.
// `matrix` expands the options into a full-width 3-column panel below the field
// (same look as the state picker) instead of the narrow scrolling list.
export const Dropdown = ({ label, icon, value, groups, onChange, placeholder, invalid, matrix } :
    { label: string, icon?: any, value: string, groups: OptGroup[], onChange: (id: string) => void, placeholder?: string, invalid?: boolean, matrix?: boolean }) => {
    const { open, closing, toggle, close, panelClass } = useExpandable();
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    const all = groups.flatMap((g) => g.items);
    const selected = all.find((o) => o.id === value);
    const pick = (o: Opt) => { if (o.disabled) return; onChange(o.id); close(); };
    return (
        <div className="cts-field" ref={ref}>
            <span>{icon ? <FontAwesomeIcon icon={icon} className="cts-label-icon" /> : null}{label}</span>
            <div className={`cts-dd ${open && !closing ? "open" : ""} ${invalid ? "invalid" : ""}`}>
                <button type="button" className="cts-dd-btn" onClick={toggle}>
                    <span className={selected ? "" : "cts-dd-placeholder"}>{selected?.label || placeholder || ""}</span>
                    <i className="cts-dd-caret" />
                </button>
                {open && matrix ? (
                    // One flat grid so every option sits on the same row height; the
                    // scope options (station / semi-stations / busbars) keep an accent
                    // so the groups stay readable without header rows.
                    <div className={`cts-menu-panel cts-dd-panel${panelClass}`}>
                        {/* Categories flow across three balanced columns, so the whole
                            list fits without scrolling. */}
                        <div className="cts-dd-grid">
                            {[1, 2, 3].map((colN) => (
                                <div className="cts-dd-col" key={colN}>
                                    {groups.filter((g) => !g.footer && (g.col || 1) === colN).map((g, gi) => (
                                        <div className="cts-dd-cat" key={gi}>
                                            {g.group ? <div className="cts-dd-grid-group">{g.group}</div> : null}
                                            {g.items.map((o) => (
                                                <button type="button" key={o.id}
                                                    className={`cts-menu-opt ${g.scope ? "scope" : ""} ${o.id === value ? "active" : ""} ${o.disabled ? "disabled" : ""}`}
                                                    onClick={() => pick(o)}>
                                                    {o.label}
                                                </button>
                                            ))}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                        {/* Footer groups (e.g. Liber) pinned bottom-left, first-column width. */}
                        {groups.filter((g) => g.footer).map((g, gi) => (
                            <div className="cts-dd-footer cts-dd-cat" key={`f${gi}`}>
                                {g.group ? <div className="cts-dd-grid-group">{g.group}</div> : null}
                                {g.items.map((o) => (
                                    <button type="button" key={o.id}
                                        className={`cts-menu-opt ${g.scope ? "scope" : ""} ${o.id === value ? "active" : ""} ${o.disabled ? "disabled" : ""}`}
                                        onClick={() => pick(o)}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : null}
                {open && !matrix ? (
                    <div className={`cts-dd-list${panelClass}`}>
                        {groups.map((g, gi) => (
                            <div key={gi}>
                                {g.group ? <div className="cts-dd-group">{g.group}</div> : null}
                                {g.items.map((o) => (
                                    <button type="button" key={o.id}
                                        className={`cts-dd-opt ${o.id === value ? "active" : ""} ${o.disabled ? "disabled" : ""}`}
                                        onClick={() => pick(o)}>
                                        {o.label}
                                    </button>
                                ))}
                            </div>
                        ))}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

// A single "state chip" for the start -> target transition visual.
export const StateChip = ({ title, state } : { title: string, state: string }) => (
    <div className="cts-state-chip">
        <span className="cts-state-kicker">{title}</span>
        <span className="cts-state-name">{state}</span>
    </div>
);

// Operative-state matrix (initial / final state picker). Aligned across the 3
// columns (Retras din exploatare | Indisponibil | Disponibil) with flow arrows.
// ids map to htScenarios.STATE_OPTIONS.
const STATE_ROWS: { r?: string, i?: string, d?: string, arrows?: boolean }[] = [
    { d: "functiune" },
    { r: "deconectat", i: "calda", d: "rezerva-calda", arrows: true },
    { r: "ssv",        i: "rece", d: "rezerva-rece",   arrows: true },
    { r: "slp" },
];

// Expandable state picker: the field keeps its grid column, clicking it floats
// the columnar matrix over both columns (`anchor` = which edge it hangs from).
export const StatePicker = ({ label, value, onChange, isValid, invalid, placeholder, anchor = "left" } :
    { label: string, value: string, onChange: (id: string) => void, isValid: (id: string) => boolean, invalid?: boolean, placeholder?: string, anchor?: "left" | "right" }) => {
    const { open, closing, toggle, close, panelClass } = useExpandable();
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) close(); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const pick = (id: string) => { onChange(id); close(); };
    const cell = (id?: string) => (id && isValid(id))
        ? <button type="button" className={`cts-menu-opt st-${id} ${id === value ? "active" : ""}`} onClick={() => pick(id)}>{stateLabelOf(id)}</button>
        : <span className="cts-menu-empty" />;
    const hArrow = (a?: string, b?: string) => (a && b && isValid(a) && isValid(b))
        ? <FontAwesomeIcon icon={faArrowsLeftRight} className="cts-menu-arrow" /> : <span />;
    const vArrow = (a?: string, b?: string) => (a && b && isValid(a) && isValid(b))
        ? <FontAwesomeIcon icon={faArrowsUpDown} className="cts-menu-arrow" /> : <span />;
    const rowVisible = (row: typeof STATE_ROWS[number]) => [row.r, row.i, row.d].some((id) => id && isValid(id));
    const rows = STATE_ROWS.filter(rowVisible);

    return (
        <div className="cts-field cts-state-field" ref={ref}>
            <span><FontAwesomeIcon icon={faClipboardList} className="cts-label-icon" />{label}</span>
            <button type="button" className={`cts-dd-btn ${open && !closing ? "cts-menu-open" : ""} ${invalid ? "invalid" : ""}`} onClick={toggle}>
                <span className={value ? "" : "cts-dd-placeholder"}>{value ? stateLabelOf(value) : (placeholder || "")}</span><i className="cts-dd-caret" />
            </button>
            {open ? (
                <div className={`cts-menu-panel cts-menu-float ${anchor}${panelClass}`}>
                    <div className="cts-menu-matrix">
                        <div className="cts-menu-hrow">
                            <div className="cts-menu-h1 mh-retras">Retras din exploatare</div>
                            <span />
                            <div className="cts-menu-h1 mh-expl">In exploatare</div>
                        </div>
                        <div className="cts-menu-hrow">
                            <span /><span />
                            <div className="cts-menu-h2 msub-indisp">Indisponibil</div>
                            <span />
                            <div className="cts-menu-h2 msub-disp">Disponibil</div>
                        </div>
                        {rows.map((row, ri) => (
                            <Fragment key={ri}>
                                <div className="cts-menu-mrow">
                                    {cell(row.r)}
                                    {row.arrows ? hArrow(row.r, row.i) : <span />}
                                    {cell(row.i)}
                                    {row.arrows ? hArrow(row.i, row.d) : <span />}
                                    {cell(row.d)}
                                </div>
                                {ri < rows.length - 1 ? (
                                    <div className="cts-menu-vrow">
                                        {vArrow(row.r, rows[ri + 1].r)}<span />
                                        {vArrow(row.i, rows[ri + 1].i)}<span />
                                        {vArrow(row.d, rows[ri + 1].d)}
                                    </div>
                                ) : null}
                            </Fragment>
                        ))}
                    </div>
                </div>
            ) : null}
        </div>
    );
};

// The cell / scope groups offered by both modules, bucketed by equipment family.
// `extra` appends module-specific groups (e.g. the training module's free roam).
export const buildCellGroups = (cells: CellDef[], lines: Opt[], en: boolean, extra: OptGroup[] = []): OptGroup[] => {
    const sorted = (list: CellDef[]) => [...list].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
    const isOE = (c: CellDef) => /^OE\d/.test(c.id);
    const isTrafo = (c: CellDef) => c.id.startsWith("Trafo") || c.id.startsWith("TSI");
    const isCupla = (c: CellDef) => !!c.isCoupler;          // C.T.V + C.L.
    const isGen = (c: CellDef) => c.id.startsWith("GENERATOR");
    const isCM = (c: CellDef) => !!c.isMeasurement;
    const rest = cells.filter((c) => !isOE(c) && !isTrafo(c) && !isCupla(c) && !isGen(c) && !isCM(c));
    return [
        // Column 1
        { col: 1, group: en ? "Generators" : "Generatoare", items: sorted(cells.filter(isGen)) },
        { col: 1, group: en ? "Electric steelworks" : "Otelarie electrica", items: sorted(cells.filter(isOE)) },
        { col: 1, group: en ? "Other lines" : "Alte linii", items: sorted(rest) },
        // Column 2 — Cuple above Trafo
        { col: 2, group: en ? "Couplings" : "Cuple", items: sorted(cells.filter(isCupla)) },
        { col: 2, group: "Trafo", items: sorted(cells.filter(isTrafo)) },
        // Column 3 — Celule de masura under Sistemul de bare
        { col: 3, group: en ? "Bar system" : "Sistemul de bare", scope: true, items: lines },
        { col: 3, group: en ? "Measuring cells" : "Celule de măsură", items: sorted(cells.filter(isCM)) },
        ...extra,
    ].filter((g) => g.items.length);
};
